<script setup lang="ts">
import { useConfigStore } from '@core/stores/config'

const store = useConfigStore()

store.skin = 'default'

definePageMeta({
  layout: 'front-page',
  public: true,
  pageTitle: {
    title: 'Word Monitor',
    subtitle: 'Sluit je aan bij ons team en maak het verschil!',
    icon: 'bx-user-plus',
    iconColor: 'primary',
  },
})

useSeoMeta({
  title: 'Word Monitor bij \'t SpeelFabrik - Solliciteer Nu',
  description: 'Sluit je aan bij ons team en maak het verschil! Ontdek waarom monitor worden bij \'t SpeelFabrik een geweldige ervaring is en hoe je kunt solliciteren.',
  ogTitle: 'Word Monitor bij \'t SpeelFabrik',
  ogDescription: 'Sluit je aan bij ons team en maak het verschil! Ontdek waarom monitor worden bij \'t SpeelFabrik een geweldige ervaring is.',
  ogType: 'website',
  ogUrl: 'https://www.tspeelfabrik.be/word-monitor',
  ogImage: 'https://www.tspeelfabrik.be/images/og-monitor.jpg',
  robots: 'index, follow',
})

useHead({
  meta: [
    { name: 'keywords', content: 'monitor worden, vacature monitor, jeugdwerk, solliciteren, SpeelFabrik, kinderopvang, speelpleinwerking, begeleider' },
  ],
  link: [
    {
      rel: 'canonical',
      href: 'https://www.tspeelfabrik.be/word-monitor',
    },
  ],
})
</script>

<template>
  <div class="landing-page-wrapper">
    <!-- 👉 Main Content Section -->
    <VContainer class="py-12">
      <VRow>
        <VCol
          cols="12"
          md="8"
          offset-md="2"
        >
          <VCard class="pa-8">
            <h2 class="text-h4 text-primary mb-6">
              Waarom monitor worden bij 't SpeelFabrik?
            </h2>

            <div class="mb-8">
              <p class="text-body-1 mb-4">
                Bij 't SpeelFabrik ben je meer dan een begeleider – je bent een spelfiguur in het avontuur van kinderen!
                Als monitor krijg je de kans om samen creatieve activiteiten te begeleiden en waardevolle ervaringen op te doen.
              </p>
            </div>

            <VRow class="mb-8">
              <VCol
                cols="12"
                md="6"
              >
                <div class="benefit-card pa-4 mb-4">
                  <VIcon
                    icon="bx-trophy"
                    size="32"
                    color="primary"
                    class="mb-3"
                  />
                  <h3 class="text-h6 mb-2">
                    Waardevolle ervaring
                  </h3>
                  <p class="text-body-2">
                    Ontwikkel leiderschaps- en communicatievaardigheden
                  </p>
                </div>
              </VCol>
              <VCol
                cols="12"
                md="6"
              >
                <div class="benefit-card pa-4 mb-4">
                  <VIcon
                    icon="bx-group"
                    size="32"
                    color="primary"
                    class="mb-3"
                  />
                  <h3 class="text-h6 mb-2">
                    Geweldig team
                  </h3>
                  <p class="text-body-2">
                    Maak deel uit van een hecht en enthousiast team
                  </p>
                </div>
              </VCol>
              <VCol
                cols="12"
                md="6"
              >
                <div class="benefit-card pa-4 mb-4">
                  <VIcon
                    icon="bx-palette"
                    size="32"
                    color="primary"
                    class="mb-3"
                  />
                  <h3 class="text-h6 mb-2">
                    Creativiteit
                  </h3>
                  <p class="text-body-2">
                    Breng je eigen ideeën en talenten in voor leuke activiteiten
                  </p>
                </div>
              </VCol>
              <VCol
                cols="12"
                md="6"
              >
                <div class="benefit-card pa-4 mb-4">
                  <VIcon
                    icon="bx-happy"
                    size="32"
                    color="primary"
                    class="mb-3"
                  />
                  <h3 class="text-h6 mb-2">
                    Plezier
                  </h3>
                  <p class="text-body-2">
                    Beleef onvergetelijke momenten met kinderen
                  </p>
                </div>
              </VCol>
            </VRow>

            <VDivider class="my-8" />

            <h2 class="text-h4 text-primary mb-6">
              Wat verwachten we van je?
            </h2>

            <VList class=" mb-8">
              <VListItem
                prepend-icon="bx-check-circle"
                class="px-0"
              >
                <VListItemTitle>Minimum 16 jaar oud</VListItemTitle>
              </VListItem>
              <VListItem
                prepend-icon="bx-check-circle"
                class="px-0"
              >
                <VListItemTitle>Een hart voor kinderen</VListItemTitle>
              </VListItem>
              <VListItem
                prepend-icon="bx-check-circle"
                class="px-0"
              >
                <VListItemTitle>Creatief en speels</VListItemTitle>
              </VListItem>
              <VListItem
                prepend-icon="bx-check-circle"
                class="px-0"
              >
                <VListItemTitle>Enthousiast en gemotiveerd</VListItemTitle>
              </VListItem>
              <VListItem
                prepend-icon="bx-check-circle"
                class="px-0"
              >
                <VListItemTitle>Verantwoordelijk en betrouwbaar</VListItemTitle>
              </VListItem>
              <VListItem
                prepend-icon="bx-check-circle"
                class="px-0"
              >
                <VListItemTitle>Teamspeler</VListItemTitle>
              </VListItem>
              <VListItem
                prepend-icon="bx-check-circle"
                class="px-0"
              >
                <VListItemTitle>Een animatordiploma is een pluspunt!</VListItemTitle>
              </VListItem>
            </VList>

            <VDivider class="my-8" />

            <h2 class="text-h4 text-primary mb-6">
              Solliciteren bij ons?
            </h2>

            <p class="text-body-1 mb-6">
              Ben je geïnteresseerd om monitor te worden bij 't SpeelFabrik?
              Neem vrijblijvend contact met ons op en vertel ons waarom jij de perfecte toevoeging bent aan ons team!
            </p>

            <VBtn
              color="primary"
              size="large"
              :to="{ name: 'contact' }"
            >
              <VIcon
                icon="bx-envelope"
                class="me-2"
              />
              Contacteer ons
            </VBtn>
          </VCard>
        </VCol>
      </VRow>
    </VContainer>
  </div>
</template>

<style lang="scss" scoped>
.benefit-card {
  border: 2px solid rgba(var(--v-theme-primary), 0.1);
  border-radius: 0.5rem;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(var(--v-theme-primary), 0.3);
    box-shadow: 0 4px 12px rgba(var(--v-theme-primary), 0.15);
    transform: translateY(-4px);
  }
}
</style>
